import React, { useState, useEffect } from 'react'

import clsx from 'clsx'
import PropTypes from 'prop-types'

import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'

import { useDialog } from '@galo-monitor/dialog'
import Loader from '@galo-monitor/web-components/Loader/LinearLoader'

import HeaderTitleClose from './HeaderTitleClose'
import useStyles from './styles'

const Modal = (props) => {
  const {
    contentText,
    children,
    id,
    open,
    escape,
    loading,
    title,
    subTitle,
    onClose,
    FooterComponent,
    position,
    headerProps,
    fullScreen,
    disableFullScreen,
    maxWidth,
    breakFullScreen,
    hideHeader,
    contentContainerStyle,
    escapeWhenLoading,
    ...otherProps
  } = props

  const classes = useStyles()

  const [fullScreenState, setFullScreenState] = useState(fullScreen)

  useEffect(() => {
    setFullScreenState(fullScreen)
  }, [fullScreen])

  const { removeDialog } = useDialog()

  const close = () => {
    removeDialog({ id })
    onClose()
  }

  const componentsProps = {
    onClose: close,
    title,
    subTitle,
    escape: escape && (escapeWhenLoading || !loading)
  }

  return (
    <Dialog
      id={ id }
      open={ open }
      onClose={ close }
      disableEscapeKeyDown={ !componentsProps.escape }
      disableBackdropClick={ !componentsProps.escape }
      disableRestoreFocus
      disableEnforceFocus
      maxWidth={ maxWidth }
      fullScreen={ fullScreenState }
      classes={ {
        paperScrollPaper: classes.paperScrollPaper,
        scrollPaper: position === 'top' ? classes.dialogTop : undefined,
        ...(otherProps.classes || {})
      } }
      { ...otherProps }
    >
      { !hideHeader && (
        <HeaderTitleClose
          fullScreen={ fullScreenState }
          onChangeFullScreen={ () => setFullScreenState((old) => !old) }
          fullScreenButton={ !disableFullScreen }
          { ...componentsProps }
          { ...headerProps }
        />
      )}

      <DialogContent
        classes={ {
          root: clsx({ [classes.withoutFooter]: !FooterComponent })
        } }
        style={ contentContainerStyle }
      >
        <div className={ classes.contentText }>{contentText}</div>
        {children}
      </DialogContent>

      {FooterComponent && (
        <DialogActions classes={ { root: classes.footerContainer } }>
          <FooterComponent { ...componentsProps } />
        </DialogActions>
      )}
      {loading && (
        <Loader loading={ loading } value={ 0 } />
      )}
    </Dialog>
  )
}

Modal.propTypes = {
  id: PropTypes.string.isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  subTitle: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  FooterComponent: PropTypes.func,
  contentText: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  children: PropTypes.element,
  escape: PropTypes.bool,
  loading: PropTypes.bool,
  escapeWhenLoading: PropTypes.bool,
  fullScreen: PropTypes.bool,
  position: PropTypes.oneOf(['middle', 'top']),
  headerProps: PropTypes.object,
  breakFullScreen: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl']),
  maxWidth: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl']),
  disableFullScreen: PropTypes.bool,
  hideHeader: PropTypes.bool,
  contentContainerStyle: PropTypes.object
}

Modal.defaultProps = {
  title: null,
  subTitle: null,
  FooterComponent: null,
  contentText: null,
  children: null,
  escape: true,
  loading: false,
  fullScreen: false,
  escapeWhenLoading: false,
  onClose: () => {},
  position: 'middle',
  headerProps: {},
  maxWidth: 'sm',
  breakFullScreen: 'xs',
  disableFullScreen: false,
  hideHeader: false,
  contentContainerStyle: {}
}

export default Modal
